<template>
    <div class="flex flex-col">
        <div class="flex min-h-screen flex-col">
            <HeaderNew class="flex-none" />

            <slot :class="pageClass" />
        </div>

        <Footer v-if="isFooterVisible" />
        <ClientOnly>
            <template v-for="modal in modalStore.modalStack" :key="modal.id">
                <component
                    v-if="modal.component"
                    :is="modal.component"
                    v-bind="modal.props"
                    @close="modalStore.closeModal(modal.id)"
                />
            </template>
        </ClientOnly>
        <ClientOnly>
            <CookieBanner />
        </ClientOnly>

        <!-- Platform-wide chat overlay - only show when logged in -->
        <ClientOnly>
            <ChatOverlay v-if="isLoggedIn" />
        </ClientOnly>

        <!-- Voice Agent FAB - temporarily disabled (not working, to be fixed later)
        <ClientOnly>
            <VoiceAgentFab v-if="isLoggedIn" />
        </ClientOnly>
        -->
    </div>
</template>

<script setup lang="ts">
import { VisibilityType } from '#shared/types/visibility'
import { useGrowniGtag } from '@/composables/analytics/gtag'
import { useMetaPixel } from '@/composables/analytics/metaPixel'
import { useApplicationStore } from '@/stores/application'
import { useModalStore } from '@/stores/modal'
import { storeToRefs } from 'pinia'

// Signed-in only: loaded on demand so anonymous visitors don't download the chat and voice code.
const ChatOverlay = defineAsyncComponent(() => import('@/components/chat/ChatOverlay.vue'))
const VoiceAgentFab = defineAsyncComponent(() => import('@/components/voice-agent/VoiceAgentFab.vue'))

const applicationStore = useApplicationStore()
const route = useRoute()

const { userProfile } = storeToRefs(applicationStore)
const isLoggedIn = computed(() => applicationStore.isLoggedIn)

const modalStore = useModalStore()

useGrowniGtag()
useMetaPixel()

const normalizedRouteName = computed(() => {
    if (typeof route.name === 'string') {
        return route.name
    }

    if (typeof route.name === 'symbol') {
        return route.name.description || 'symbol-route'
    }

    return 'unknown-route'
})

const pageClass = computed(() => [`page-${normalizedRouteName.value}`])
const isFooterVisible = computed(() => !route.name?.toString()?.includes('messages'))
const _isOrganisation = computed(() => applicationStore.userOrgs?.length !== 0)
const _isMentor = computed(() => userProfile.value?.mentorProfile?.visibility === VisibilityType.published)
const _isVolunteer = computed(() => userProfile.value?.volunteerProfile?.visibility === VisibilityType.published)

const trackGtmEvent = ref(false)
const trackGtmCode = ref('')

useHead(() => {
    if (!trackGtmEvent.value) return {}

    return {
        script: [
            {
                innerHTML: `
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({${trackGtmCode.value}});
        `,
                type: 'text/javascript'
            }
        ]
    }
})

onMounted(() => {
    setTimeout(() => {
        trackGtmCode.value =
            `event: "user_info", user_id: ` +
            (userProfile.value ? `'${userProfile.value.id}'` : `"not_set"`) +
            `, is_mentor: ${_isMentor.value}, is_volunteer: ${_isVolunteer.value}` +
            ', is_organization: ' +
            _isOrganisation.value
        trackGtmEvent.value = true
    }, 1000)
})
</script>
